/* RESPONSIBLE TEAM: team-frontend-tech */

export function loadScript(url: string, type = 'module'): Promise<void> {
  return new Promise((resolve, reject) => {
    let script = document.createElement('script');
    script.src = url;
    script.type = type;
    script.onload = () => resolve();
    let path = url;
    try {
      path = new URL(url).pathname;
    } catch {
      // keep original url if not parseable
    }
    script.onerror = () => reject(new Error(`Failed to load script: ${path}`));
    document.head.appendChild(script);
  });
}

// Loads a script and captures any synchronous runtime errors thrown during
// module evaluation (e.g. syntax errors, missing imports). Without this,
// those errors surface as uncaught exceptions with no connection to the script load.
export async function loadScriptWithErrorCapture(url: string, type = 'module'): Promise<void> {
  let scriptError: Error | undefined;
  let errorHandler = (event: ErrorEvent) => {
    if (scriptError) {
      return;
    }
    if (event.error instanceof Error) {
      scriptError = event.error;
    } else {
      let location = event.filename
        ? `\n    at ${event.filename}:${event.lineno}:${event.colno}`
        : '';
      scriptError = new Error(`${event.message}${location}`);
    }
  };

  window.addEventListener('error', errorHandler);
  try {
    await loadScript(url, type);
  } finally {
    window.removeEventListener('error', errorHandler);
  }

  if (scriptError) {
    throw scriptError;
  }
}

export async function loadStylesheet(
  url: string,
  parent: HTMLElement | ShadowRoot = document.head,
): Promise<void> {
  try {
    await new Promise<void>((resolve, reject) => {
      let link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      link.onload = () => resolve();
      link.onerror = (error) => reject(error);
      parent.appendChild(link);
    });
  } catch (error) {
    // MSW's service worker returns opaque responses for cross-origin stylesheets,
    // which triggers <link> onerror with a bare Event (no status/message).
    // The stylesheet still applies, so we can safely ignore it when MSW is active.
    let isMswActive = navigator.serviceWorker?.controller?.scriptURL.includes('mockServiceWorker');
    if (isMswActive) {
      return;
    }
    throw error;
  }
}
